import { useRef, useState } from 'react'
import { Box, Stack, TextField, Button, Typography } from '@mui/material'

import { sendMessage } from '../services/api'
import { trackEnquiryConversion } from '../shared/lib/googleAds'
import { getAnalyticsSessionId } from './PageViewTracker'
import { useSetPopup } from './Popup'

const ContactForm = ({
    defaultMessage = '',
    carName = '',
    submitLabel = 'Send message',
    onSuccess,
    variant = 'default',
}) => {
    const setPopup = useSetPopup()

    const [pending, setPending] = useState(false)
    const [errors, setErrors] = useState({})
    const [submitError, setSubmitError] = useState("")

    const nameRef = useRef()
    const emailRef = useRef()
    const phoneRef = useRef()
    const messageRef = useRef()

    const isPanel = variant === 'panel'

    const fieldSx = isPanel
        ? {
              '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f4f7fb',
                  borderRadius: 2,
                  '& fieldset': {
                      borderColor: 'rgba(0, 4, 53, 0.1)',
                  },
                  '&:hover fieldset': {
                      borderColor: 'rgba(0, 163, 255, 0.45)',
                  },
                  '&.Mui-focused fieldset': {
                      borderColor: '#00A3FF',
                  },
              },
          }
        : undefined

    const onSend = (e, isPending) => {
        e.preventDefault(true)

        if (isPending) return
        setPending(true)
        setErrors({})
        setSubmitError("")

        sendMessage(
            nameRef.current.value,
            emailRef.current.value,
            phoneRef.current.value,
            messageRef.current.value,
            carName || undefined,
            getAnalyticsSessionId()
        )
            .then(() => {
                nameRef.current.value = ''
                emailRef.current.value = ''
                phoneRef.current.value = ''
                messageRef.current.value = ''

                setPopup({
                    title: 'Enquiry received',
                    message:
                        'Thank you for your interest in Stargate Rentals. We have received your enquiry and will be in touch shortly. For urgent help, call us on 0191 743 2274.',
                })

                trackEnquiryConversion()

                if (onSuccess) onSuccess()
            })
            .catch((err) => {
                const fieldErrors = err?.response?.data?.errors
                if (fieldErrors && typeof fieldErrors === "object") {
                    setErrors(fieldErrors)
                    return
                }

                const message =
                    err?.response?.data?.error ||
                    (err?.response?.status === 429
                        ? "Too many enquiries sent recently. Please wait a few minutes and try again."
                        : "We could not send your enquiry. Please try again or call us on 0191 743 2274.")
                setSubmitError(message)
            })
            .finally(() => setPending(false))
    }

    return (
        <Box component="form" onSubmit={(e) => onSend(e, pending)} disabled={pending}>
            <Stack direction="column" rowGap={2}>
                <TextField
                    fullWidth
                    color="secondary"
                    size="large"
                    variant="outlined"
                    type="text"
                    label="Your name"
                    inputRef={nameRef}
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={fieldSx}
                />
                <TextField
                    fullWidth
                    color="secondary"
                    size="large"
                    variant="outlined"
                    type="email"
                    label="Your email"
                    inputRef={emailRef}
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={fieldSx}
                />
                <TextField
                    fullWidth
                    color="secondary"
                    size="large"
                    variant="outlined"
                    type="text"
                    label="Your phone"
                    inputRef={phoneRef}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    sx={fieldSx}
                />
                <TextField
                    multiline
                    rows={isPanel ? 4 : 5}
                    fullWidth
                    color="secondary"
                    size="large"
                    variant="outlined"
                    type="text"
                    label="Message"
                    inputRef={messageRef}
                    defaultValue={defaultMessage}
                    error={!!errors.message}
                    helperText={errors.message}
                    sx={fieldSx}
                />
                <Box component="div">
                    {submitError && (
                        <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
                            {submitError}
                        </Typography>
                    )}
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        color="secondary"
                        disabled={pending}
                        fullWidth={isPanel}
                        sx={{
                            borderRadius: 999,
                            px: 3,
                            py: 1.35,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '1rem',
                        }}
                    >
                        {pending ? 'Sending…' : submitLabel}
                    </Button>
                </Box>
            </Stack>
        </Box>
    )
}

export default ContactForm
